/**
 * Notification Bell Component
 * Modern notification dropdown with real-time updates
 */

import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  LocalHospital as LocalHospitalIcon,
  Medication as MedicationIcon,
  Vaccines as VaccinesIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import NotificationService, { Notification, NotificationStats } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../services/logger';

const log = logger.createScope('NotificationBell');

interface NotificationBellProps {
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  size = 'medium',
  showLabel: _showLabel = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    total: 0,
    unread: 0,
    byType: {},
    byPriority: {}
  });
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  // Subscribe to notifications
  useEffect(() => {
    if (!currentUser?.uid) {
      log.debug('No user authenticated, skipping subscription');
      return;
    }

    log.debug('Setting up notification subscription for user', { userId: currentUser.uid });

    // Force refresh to ensure we get the latest filtered notifications
    NotificationService.refreshNotifications(currentUser.uid).catch(err => {
      log.error('Error refreshing notifications', err as Error, { userId: currentUser.uid });
    });

    const unsubscribe = NotificationService.subscribeToNotifications(
      currentUser.uid,
      (newNotifications) => {
        log.debug('Received notifications update', { userId: currentUser.uid, count: newNotifications.length });
        setNotifications(newNotifications);
      }
    );

    const unsubscribeStats = NotificationService.subscribeToStats(
      currentUser.uid,
      (newStats) => {
        log.debug('Received stats update', { userId: currentUser.uid, stats: newStats });
        setStats(newStats);
      }
    );

    return () => {
      log.debug('Cleaning up notification subscriptions', { userId: currentUser?.uid });
      unsubscribe();
      unsubscribeStats();
    };
  }, [currentUser?.uid]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      await NotificationService.markAsRead(notification.id, currentUser?.uid || undefined);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }

    handleClose();
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser?.uid) return;
    
    setLoading(true);
    try {
      await NotificationService.markAllAsRead(currentUser.uid);
    } catch (error) {
      log.error('Error marking all as read', error as Error, { userId: currentUser?.uid });
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type: string | undefined, priority: string) => {
    const iconProps = { fontSize: 'small' as const };
    
    switch (type) {
      case 'lab_result':
        return priority === 'urgent' || priority === 'high' 
          ? <WarningIcon {...iconProps} color="warning" />
          : <CheckCircleIcon {...iconProps} color="success" />;
      case 'appointment':
        return <ScheduleIcon {...iconProps} color="primary" />;
      case 'vet_visit':
        return <LocalHospitalIcon {...iconProps} color="info" />;
      case 'medication':
        return <MedicationIcon {...iconProps} color="secondary" />;
      case 'vaccination':
        return <VaccinesIcon {...iconProps} color="success" />;
      case 'review_request':
        return <AssignmentIcon {...iconProps} color="warning" />;
      case 'quarantine_alert':
        return <SecurityIcon {...iconProps} color={priority === 'high' ? 'error' : 'warning'} />;
      case 'reminder':
        return <ScheduleIcon {...iconProps} color="primary" />;
      case 'expense':
        return <LocalHospitalIcon {...iconProps} color="info" />;
      case 'system':
        return <SettingsIcon {...iconProps} color="info" />;
      default:
        return <InfoIcon {...iconProps} color="info" />;
    }
  };


  const formatTimeAgo = (date: Date) => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // In dropdown: show only unread so read ones "disappear" from the bell.
  // User can open "View All Notifications" to see read items and delete them.
  const unreadInDropdown = notifications.filter((n) => !n.read);



  const iconSize = size === 'small' ? 20 : size === 'large' ? 32 : 24;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClick}
          sx={{
            width: isMobile ? 48 : iconSize + 8, // Minimum 48px on mobile for touch
            height: isMobile ? 48 : iconSize + 8,
            minWidth: isMobile ? 48 : undefined,
            minHeight: isMobile ? 48 : undefined,
            color: 'inherit',
            // Add haptic feedback on mobile
            '@media (hover: none) and (pointer: coarse)': {
              '&:active': {
                transform: 'scale(0.95)',
                transition: 'transform 0.1s',
              }
            }
          }}
        >
          <Badge 
            badgeContent={stats.unread} 
            color="error"
            max={99}
            invisible={stats.unread === 0}
          >
            {stats.unread > 0 ? (
              <NotificationsActiveIcon sx={{ fontSize: iconSize }} />
            ) : (
              <NotificationsIcon sx={{ fontSize: iconSize }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: isMobile ? '90vw' : 380,
            maxHeight: '85vh',
            height: 'auto',
            mt: 1,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        MenuListProps={{
          sx: {
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            maxHeight: '85vh',
            overflow: 'hidden'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Notifications
            </Typography>
            {stats.unread > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                disabled={loading}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 1.5
                }}
              >
                Mark all read
              </Button>
            )}
          </Box>
          
          {stats.unread > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {stats.unread} unread
            </Typography>
          )}
        </Box>

        {/* Notifications List - Scrollable (bounded height so footer stays visible) */}
        <Box 
          sx={{ 
            flex: 1,
            minHeight: 0,
            maxHeight: '50vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0,0,0,0.05)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: 4,
              '&:hover': {
                background: 'rgba(0,0,0,0.3)',
              },
            },
          }}
        >
          {unreadInDropdown.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1, opacity: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                No new notifications
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                Read notifications are in View All — you can delete them there.
              </Typography>
            </Box>
          ) : (
            unreadInDropdown.map((notification, index) => {
              const isAttentionNeeded = notification.priority === 'urgent' || 
                                       notification.priority === 'high' ||
                                       notification.category === 'health_alert' ||
                                       (notification.data?.isInformational !== true && notification.data?.followUpNeeded);
              
              const _isProcessed = notification.data?.isInformational === true || 
                                 notification.title?.includes('Processed') ||
                                 notification.title?.includes('processed');

              return (
                <Box key={notification.id}>
                  <MenuItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      minHeight: 'auto',
                      backgroundColor: notification.read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                      borderLeft: !notification.read ? '3px solid' : '3px solid transparent',
                      borderLeftColor: !notification.read 
                        ? isAttentionNeeded ? 'error.main' : 'primary.main'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 1.5 }}>
                      {/* Icon */}
                      <Box 
                        sx={{ 
                          mt: 0.25,
                          color: isAttentionNeeded ? 'error.main' : 'primary.main',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {getNotificationIcon(notification.type, notification.priority)}
                      </Box>
                      
                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: notification.read ? 500 : 600,
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            mb: 0.25,
                            color: 'text.primary'
                          }}
                        >
                          {notification.title}
                        </Typography>
                        
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            display: 'block',
                            mb: 0.75
                          }}
                        >
                          {formatTimeAgo(notification.createdAt)}
                        </Typography>

                        {/* Action label if available */}
                        {notification.actionLabel && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.75rem',
                              color: 'primary.main',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            {notification.actionLabel} →
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                  
                  {index < unreadInDropdown.length - 1 && (
                    <Divider sx={{ mx: 2, borderColor: 'divider' }} />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer - visible when there are any notifications so user can open full page to see read items and delete */}
        {notifications.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 1.5,
              backgroundColor: 'background.default',
            }}
          >
            <Button
              fullWidth
              size="small"
              onClick={() => {
                navigate('/notifications');
                handleClose();
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              View All Notifications
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontSize: '0.7rem' }}>
              Read items stay in View All until you delete them (or they expire).
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
